import { Component, signal, ChangeDetectionStrategy } from '@angular/core';
import { MatPaginatorIntl, MatPaginatorModule, PageEvent } from '@angular/material/paginator';
import { MatTableModule } from '@angular/material/table';
import { RouterModule } from '@angular/router';
import { Observable, Subscription, finalize } from 'rxjs';
import { OuiButtonModule } from 'omnium-ui/button';
import { OuiSnackbarService } from 'omnium-ui/snackbar';
import { OuiTableModule } from 'omnium-ui/table';
import { appRoutePaths } from '../../../../app.paths';
import { ConsultantCardComponent } from '../../../../core/components/consultant-card/consultant-card.component';
import { InvestorCardComponent } from '../../../../core/components/investor-card/investor-card.component';
import {
  MinotaureRecueilCrossConsultantInvestor,
  MinotaureRecueilCrossConsultantPage,
  MinotaureRecueilCrossConsultantRow,
} from '../../../../core/models/api.models';
import { DoublonsBusterApiService } from '../../../../core/services/doublons-buster-api.service';
import { StateCardComponent } from '../../shared/state-card/state-card.component';
import { AtlasDuplicateListPageBase } from '../../atlas/shared/atlas-duplicate-list-page.base';
import { createFrenchPaginatorIntl } from '../../atlas/shared/atlas-list-page.paginator';

type RecueilConsultant = {
  key: string;
  consultantId?: number | null;
  consultantCode?: string | null;
  consultantName?: string | null;
  consultantStatusCode?: string | null;
};

@Component({
  selector: 'dbw-recueil-cross-consultant-page',
  imports: [
    MatPaginatorModule,
    MatTableModule,
    RouterModule,
    OuiButtonModule,
    OuiTableModule,
    ConsultantCardComponent,
    InvestorCardComponent,
    StateCardComponent,
  ],
  providers: [
    {
      provide: MatPaginatorIntl,
      useFactory: createFrenchPaginatorIntl,
    },
  ],
  templateUrl: './recueil-cross-consultant.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['../../atlas/shared/atlas-duplicate-list-page.shared.scss', './recueil-cross-consultant.component.scss'],
})
export class RecueilCrossConsultantPageComponent extends AtlasDuplicateListPageBase<MinotaureRecueilCrossConsultantPage> {
  readonly displayedColumns = ['recueil', 'investors', 'consultants'];
  readonly appRoutePaths = appRoutePaths;
  readonly exporting = signal(false);

  private exportSubscription?: Subscription;

  constructor(
    private readonly apiService: DoublonsBusterApiService,
    private readonly snackbarService: OuiSnackbarService
  ) {
    super();
  }

  override ngOnDestroy(): void {
    super.ngOnDestroy();
    this.exportSubscription?.unsubscribe();
  }

  onPageChange(event: PageEvent): void {
    this.pageIndex.set(event.pageIndex);
    this.pageSize.set(event.pageSize);
    this.loadPage();
  }

  exportCsv(): void {
    if (this.exporting()) {
      return;
    }

    this.exporting.set(true);
    this.exportSubscription?.unsubscribe();
    this.exportSubscription = this.apiService
      .exportMinotaureRecueilCrossConsultant(this.buildFilters())
      .pipe(finalize(() => this.exporting.set(false)))
      .subscribe({
        next: blob => {
          const downloadUrl = URL.createObjectURL(blob);
          const anchor = document.createElement('a');
          anchor.href = downloadUrl;
          anchor.download = `recueil-inter-consultants-${new Date().toISOString().replace(/[:.]/g, '-')}.csv`;
          anchor.click();
          URL.revokeObjectURL(downloadUrl);
          this.snackbarService.open('Export CSV terminé.', 'success');
        },
        error: () => {
          this.snackbarService.open("L'export CSV a échoué.", 'error');
        },
      });
  }

  getInvestors(row: MinotaureRecueilCrossConsultantRow): MinotaureRecueilCrossConsultantInvestor[] {
    const investorsById = new Map<number, MinotaureRecueilCrossConsultantInvestor>();

    for (const investor of row.investors) {
      if (!investorsById.has(investor.investorId)) {
        investorsById.set(investor.investorId, investor);
      }
    }

    return [...investorsById.values()].sort((left, right) => left.investorId - right.investorId);
  }

  getRecueilTitle(row: MinotaureRecueilCrossConsultantRow): string {
    const names = this.getInvestors(row)
      .map(investor => investor.displayName?.trim())
      .filter((name): name is string => !!name);
    const distinctNames = [...new Set(names)].slice(0, 2);

    return distinctNames.length > 0
      ? `Recueil ${distinctNames.join(' / ')}`
      : `Recueil metadata #${row.metadataId}`;
  }

  getConsultants(row: MinotaureRecueilCrossConsultantRow): RecueilConsultant[] {
    return this.getInvestors(row).map(investor => ({
      key: `${investor.investorId}:${this.getConsultantKey(investor) ?? 'missing'}`,
      consultantId: investor.consultantId,
      consultantCode: investor.consultantCode,
      consultantName: investor.consultantName,
      consultantStatusCode: investor.consultantStatusCode,
    }));
  }

  protected override loadPageRequest(pageIndex: number, pageSize: number): Observable<MinotaureRecueilCrossConsultantPage> {
    return this.apiService.getMinotaureRecueilCrossConsultantPage(pageIndex, pageSize, this.buildFilters());
  }

  protected override getLoadErrorMessage(error: unknown): string {
    const httpError = error as { error?: { detail?: string } | string };

    return typeof httpError?.error === 'string'
      ? httpError.error
      : (httpError?.error?.detail ?? 'Impossible de charger le reporting recueil inter-consultants.');
  }

  protected override getPrimaryFilterSummary(): string | null {
    return null;
  }

  protected trackRow(_index: number, row: MinotaureRecueilCrossConsultantRow): string {
    return row.metadataId.toString();
  }

  private buildFilters(): {
    metadataId?: number | null;
    recueilKind?: string | null;
    recueilObjectId?: number | null;
    investorId?: number | null;
    consultantId?: number | null;
    consultantCode?: string;
    consultantName?: string;
  } {
    return {};
  }

  private getConsultantKey(investor: MinotaureRecueilCrossConsultantInvestor): string | null {
    if (investor.consultantId !== null && investor.consultantId !== undefined) {
      return `id:${investor.consultantId}`;
    }

    const consultantCode = investor.consultantCode?.trim();
    const consultantName = investor.consultantName?.trim();

    return consultantCode || consultantName
      ? `label:${consultantCode ?? ''}:${consultantName ?? ''}`
      : null;
  }
}
